import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { Player } from "../../types/game";
import { useGameStore } from "../../store/gameStore";
import { useLanguage } from "../../i18n/LanguageContext";

interface Props {
  player: Player;
  show: boolean;
}

export default function TurnBanner({ player, show }: Props) {
  const { t } = useLanguage();
  useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => useGameStore.setState({ showTurnBanner: false }), 2200);
    return () => clearTimeout(t);
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          {/* blurred backdrop */}
          <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" />

          <motion.div
            className="relative flex flex-col items-center gap-4"
            initial={{ scale: 0.6, y: 40 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.8, y: -30, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 22 }}
          >
            {/* glow ring */}
            <motion.div
              className="absolute w-48 h-48 rounded-full bg-earnings/20 blur-3xl"
              animate={{ scale: [1, 1.3, 1] }}
              transition={{ repeat: Infinity, duration: 1.4 }}
            />

            <motion.div
              className="text-8xl drop-shadow-2xl"
              animate={{ rotate: [-8, 8, -8] }}
              transition={{ repeat: Infinity, duration: 0.9, ease: "easeInOut" }}
            >
              {player.avatarKey}
            </motion.div>

            <div className="text-center">
              <motion.div
                className="text-4xl font-black text-white tracking-tight drop-shadow-lg"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
              >
                {player.name}
              </motion.div>
              <motion.div
                className="text-earnings font-bold text-lg mt-1 tracking-widest uppercase"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                {t.action.yourTurn}
              </motion.div>
            </div>

            {/* animated dots */}
            <motion.div className="flex gap-2 mt-2">
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className="w-2 h-2 rounded-full bg-earnings/60"
                  animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.2, 0.8] }}
                  transition={{ repeat: Infinity, duration: 0.8, delay: i * 0.2 }}
                />
              ))}
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
